import React, { useState, useEffect } from "react";
import Select, {
  components,
  DropdownIndicatorProps,
  GroupBase,
  Props,
} from "react-select";

import classNames from "classnames";
// @ts-ignore
import ColorSchema from "../../kits/color/index.ts";
import "./inputStyle.scss";

type TextType = "none" | "error" | "guide";

type intersectionProps = {
  title?: string;
  messageText?: string;
  textType?: TextType;
  textLength?: number;
  haveIcon?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  containerClassName?: string;
  labelClassName?: string;
  inputHeight?: number;
  resize?: boolean;
  mainStyle?: "borderStyle" | "shadowStyle";
};

type CustomInputProps = intersectionProps &
  React.InputHTMLAttributes<HTMLInputElement>;

type CustomTextAreaProps = intersectionProps &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>;
const Layout: React.FC<
  intersectionProps & {
    isInput?: boolean;
    id?: string;
    disabled?: boolean;
    value?: string | number | readonly string[];
    children?: JSX.Element | JSX.Element[];
  }
> = ({
  //text to show in title
  title = "",
  //error or warning text to show when error happened
  messageText = "",
  //limit character in input
  textLength = Infinity,
  isInput = false,
  haveIcon = false,
  textType = "none",
  containerClassName = "",
  labelClassName = "",
  disabled = false,
  id,
  icon,
  iconPosition = "left",
  value,
  children,
  inputHeight = 48,
}) => {
  return (
    <div
      className={`inputs ${containerClassName} ${haveIcon ? "have-icon" : ""}`}
      style={{ width: "100%", position: "relative" }}
    >
      {title !== "" && (
        <label
          className={labelClassName}
          style={{ marginBottom: 4, height: 21 }}
          htmlFor={id}
        >
          {title}
        </label>
      )}
      {/* icon */}

      {haveIcon && (
        <label
          className={`input-icon ${
            disabled && "disable-style"
          } d-flex align-items-center`}
          style={{
            opacity: "0.4",
            pointerEvents: "inherit",
            right: iconPosition == "right" ? 16 : "auto",
            left: iconPosition == "left" ? 4 : "auto",
            // top: title != "" ? 36 : 10,
            top:
              title == ""
                ? `${10 + Math.ceil((inputHeight - 40) / 2)}%`
                : "37%",
          }}
          htmlFor={id}
        >
          {icon}
          {/* <Instagram color={ColorSchema.gray_1} /> */}
        </label>
      )}
      {children}
      {/* massage */}
      {(textType !== "none" || textLength !== Infinity) && (
        <div
          className="mr-t-4"
          style={{
            display: "flex",
            justifyContent:
              textType === "none" && disabled ? "end" : "space-between",
          }}
        >
          {textType !== "none" && !disabled && (
            <label
              className={`${
                textType === "guide" ? "warning-style" : "error-style"
              } massage desc-l`}
              style={{ width: textLength !== Infinity ? "95%" : "inherit" }}
            >
              {messageText}
            </label>
          )}
          {/* text length */}
          {textLength !== Infinity && isInput && (
            <label
              className="text-length desc-l"
              style={{
                width: textType !== "none" ? 0 : "inherit",
              }}
            >
              {(value + "").length}/{textLength}
            </label>
          )}
        </div>
      )}
    </div>
  );
};

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      //text to show in title
      title = "",
      //error or warning text to show when error happened
      messageText = "",
      //limit character in input
      textLength = Infinity,
      haveIcon = false,
      icon,
      iconPosition,
      textType = "none",
      containerClassName = "",
      labelClassName = "",
      className,
      placeholder,
      style,
      value,
      onChange,
      onFocus,
      onBlur,
      inputHeight = 48,
      mainStyle = "border",
      ...otherProps
    },
    ref
  ) => {
    const [preview, setPreview] = useState<string>("");
    otherProps.id =
      otherProps.id || `${1000 + Math.floor(Math.random() * 1000)}`;

    useEffect(() => {
      setPreview(placeholder || "");
    }, [placeholder]);

    return (
      <Layout
        id={otherProps.id}
        title={title}
        messageText={messageText}
        textLength={textLength}
        haveIcon={haveIcon}
        textType={textType}
        containerClassName={containerClassName}
        labelClassName={labelClassName}
        isInput
        icon={icon}
        iconPosition={iconPosition}
        inputHeight={inputHeight}
      >
        {/* input */}
        <input
          ref={ref}
          className={`input-style txt-t2-l ${
            mainStyle == "border" ? "borderStyle" : "shadowStyle"
          } ${
            otherProps.disabled
              ? "disable-style"
              : textType === "error"
              ? "input-border-error"
              : textType === "guide"
              ? "input-border-warning"
              : ""
          } ${
            haveIcon && iconPosition == "right" ? "pd-r-icon" : "pd-r-12"
          } ${className}`}
          placeholder={preview}
          value={value}
          onChange={(e) => {
            if (e.target.value.length <= textLength) {
              if (typeof onChange !== "undefined") {
                onChange(e);
              }
            }
          }}
          onBlur={(e) => {
            setPreview(placeholder || "");
            if (typeof onBlur !== "undefined") {
              onBlur(e);
            }
          }}
          onFocus={(e) => {
            setPreview("");
            if (typeof onFocus !== "undefined") {
              onFocus(e);
            }
          }}
          style={{
            width: "100%",
            height: inputHeight,
            ...style,
          }}
          {...otherProps}
        />
      </Layout>
    );
  }
);

const CustomTextArea = React.forwardRef<
  HTMLTextAreaElement,
  CustomTextAreaProps
>(
  (
    {
      //text to show in title
      title = "",
      //error or warning text to show when error happened
      messageText = "",
      //limit character in input
      textLength = Infinity,
      haveIcon = false,
      textType = "none",
      containerClassName = "",
      labelClassName = "",
      className,
      placeholder,
      style,
      value,
      onChange,
      onFocus,
      onBlur,
      resize = false,
      mainStyle = "border",
      ...otherProps
    },
    ref
  ) => {
    const [preview, setPreview] = useState<string>(placeholder || "");
    otherProps.id =
      otherProps.id || `${1000 + Math.floor(Math.random() * 1000)}`;

    return (
      <Layout
        id={otherProps.id}
        title={title}
        messageText={messageText}
        textLength={textLength}
        haveIcon={haveIcon}
        textType={textType}
        containerClassName={containerClassName}
        labelClassName={labelClassName}
      >
        {/* textarea */}
        <textarea
          ref={ref}
          className={`textarea-style txt-t2-l ${
            mainStyle == "border" ? "borderStyle" : "shadowStyle"
          } ${
            otherProps.disabled
              ? "disable-style"
              : textType === "error"
              ? "input-border-error"
              : textType === "guide"
              ? "input-border-warning"
              : ""
          } ${haveIcon ? "pd-r-40" : "pd-r-12"} ${className}`}
          placeholder={preview}
          value={value}
          onChange={(e) => {
            if (e.target.value.length <= textLength) {
              if (typeof onChange !== "undefined") {
                onChange(e);
              }
            }
          }}
          onBlur={(e) => {
            setPreview(placeholder || "");
            if (typeof onBlur !== "undefined") {
              onBlur(e);
            }
          }}
          onFocus={(e) => {
            setPreview("");
            if (typeof onFocus !== "undefined") {
              onFocus(e);
            }
          }}
          style={{
            width: "100%",
            resize: resize ? "both" : "none",
            ...style,
          }}
          {...otherProps}
        />
      </Layout>
    );
  }
);

type OptionsType = { [key: string]: string } & { label: string; value: string };
type CustomSelectProps = intersectionProps &
  Props<OptionsType, boolean, GroupBase<OptionsType>>;
// React.SelectHTMLAttributes<HTMLSelectElement> &
const CustomSelect = React.forwardRef<any, CustomSelectProps>(
  (
    {
      //text to show in title
      title = "",
      //error or warning text to show when error happened
      messageText = "",
      //limit character in input
      textLength = Infinity,
      haveIcon = false,
      textType = "none",
      icon,
      containerClassName = "",
      labelClassName = "",
      className,
      styles,
      components: customComponents,
      inputHeight = 48,
      mainStyle = "border",
      ...otherProps
    },
    ref
  ) => {
    otherProps.id =
      otherProps.id || `${1000 + Math.floor(Math.random() * 1000)}`;
    const DropdownIndicator = (
      props: DropdownIndicatorProps<
        OptionsType,
        boolean,
        GroupBase<OptionsType>
      >
    ) => {
      return (
        <components.DropdownIndicator {...props}>
          {icon}
        </components.DropdownIndicator>
      );
    };

    return (
      <Layout
        id={otherProps.id}
        title={title}
        messageText={messageText}
        textLength={textLength}
        haveIcon={haveIcon}
        textType={textType}
        containerClassName={containerClassName}
        labelClassName={labelClassName}
      >
        {/* textarea */}
        <Select
          className={classNames("select-style", className)}
          components={{
            DropdownIndicator: DropdownIndicator,
            ...customComponents,
          }}
          styles={{
            ...styles,
            control: (baseStyles, state) => ({
              ...baseStyles,
              boxShadow:
                mainStyle != "shadow"
                  ? "none"
                  : "0px 8px 32px rgba(0, 0, 0, 0.1)",
              borderColor: state.isFocused
                ? '#D7D7D5'
                : textType == "error"
                ? ColorSchema.red_1
                : mainStyle == "border"
                ? ColorSchema.gray_6
                : "transparent",
              borderColor:  "var(--bs-border-color-translucent)",
                "&:hover": {
                    borderColor:  "var(--bs-border-color-translucent)",
                },
              "&:hover": {
                borderColor:
                  textType == "error" ? ColorSchema.red_1 : ColorSchema.gray_6,
              },
              minHeight: inputHeight,
              height: otherProps.isMulti ? "auto" : inputHeight,
              borderRadius: 8,
              ...styles?.control,
            }),
          }}
          {...otherProps}
        />
      </Layout>
    );
  }
);

export { CustomInput, CustomTextArea, CustomSelect };

export type { CustomInputProps, CustomTextAreaProps };
